import { useEffect, useRef } from 'react';

interface SubscribableState<T> {
	data: T;
	subscribe: (callback: (state: SubscribableState<T>) => void) => () => void;
	[key: string]: any;
}

interface Store<T> {
	getState: () => SubscribableState<T>;
}

/**
 * Zustand store를 구독하여 ref로 DOM을 직접 업데이트하는 훅
 * React 리렌더링 없이 실시간 데이터를 표시할 때 사용
 *
 * @param {Store<T>} store - Zustand store (예: hudStore, locationStore)
 * @param {Function} selector - 데이터를 선택하는 함수 (state) => value
 * @param {Function} formatter - (선택) 값을 포맷팅하는 함수 (value) => string
 * @returns {React.RefObject} - DOM element에 연결할 ref
 *
 * @example
 * // 단순 값
 * const speedRef = useStoreRef(hudStore, (state) => state.data.speed);
 * <span ref={speedRef}>-</span>
 *
 * @example
 * // 포맷팅 적용
 * const speedRef = useStoreRef(
 *   hudStore,
 *   (state) => state.data.speed,
 *   (speed) => `${speed} km/h`
 * );
 * <span ref={speedRef}>- km/h</span>
 */
export const useStoreRef = <T, R>(
	store: Store<T>,
	selector: (state: SubscribableState<T>) => R,
	formatter: ((value: R) => string | number) | null = null,
) => {
	const ref = useRef<HTMLElement>(null);

	useEffect(() => {
		// 스토어 구독: 데이터 변경 시 DOM 직접 업데이트
		const unsub = store.getState().subscribe((state) => {
			if (ref.current) {
				const value = selector(state);
				const displayValue = formatter ? formatter(value) : value;
				ref.current.textContent = String(displayValue);
			}
		});

		// 초기값 설정
		if (ref.current) {
			const initialState = store.getState();
			const value = selector(initialState);
			const displayValue = formatter ? formatter(value) : value;
			ref.current.textContent = String(displayValue);
		}

		return () => unsub();
	}, [store, selector, formatter]);

	return ref;
};

/**
 * 여러 값을 조합하여 표시하는 훅
 *
 * @param {Store<T>} store - Zustand store
 * @param {Function} formatter - (state) => string - state 전체를 받아서 포맷팅
 * @returns {React.RefObject}
 *
 * @example
 * const locationRef = useStoreRefMulti(
 *   locationStore,
 *   (state) => `${state.data.lat}, ${state.data.lon}`
 * );
 * <span ref={locationRef}>-, -</span>
 */
export const useStoreRefMulti = <T>(
	store: Store<T>,
	formatter: (state: SubscribableState<T>) => string | number,
) => {
	const ref = useRef<HTMLElement>(null);

	useEffect(() => {
		const unsub = store.getState().subscribe((state) => {
			if (ref.current) {
				ref.current.textContent = String(formatter(state));
			}
		});

		// 초기값 설정
		if (ref.current) {
			const initialState = store.getState();
			ref.current.textContent = String(formatter(initialState));
		}

		return () => unsub();
	}, [store, formatter]);

	return ref;
};
